import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import JudgesLight from "./JudgesLight";
import SealedPlaces from "./SealedPlaces";
import SiteFooter from "./SiteFooter";

/**
 * Below the fold on a Practice door — playforward.app, stageward.app.
 *
 * Founder's ask: the same information under every .app so someone can read
 * before they click in, with the World's brand, offer and promise carried
 * through rather than a bare gate. This is the universal slab; only the three
 * cards at the top know which Place they are standing on.
 */
const CARDS = (place) => [
  {
    n: "01",
    t: "You are on one door of a World",
    b: `Endeavorum is a World with eight Places in it. ${place.name} is one of them, and it has its own front door — this one. Whatever you spend, earn or say here is held at the World level, so it follows you into every other Place.`,
  },
  {
    n: "02",
    t: `In here, the Practice is ${place.practice}`,
    b: place.body,
  },
  {
    n: "03",
    t: "You leave holding a Blueprint",
    b: "Not a plan you'll admire and abandon. A bounded piece of work with a date on it, ordered the way a builder schedules a build, and yours to export and execute in the real world.",
  },
];

export const PortalBelow = ({ place, worldOrigin }) => (
  <div
    data-testid="portal-below"
    className="relative z-10 bg-[var(--ink)]"
  >
    <section className="mx-auto max-w-[1180px] px-6 py-16 md:px-10 md:py-24">
      <p className="mono mb-3 text-[10px] uppercase tracking-[0.32em] text-[var(--brand-gold)]">
        Endeavor to Design, Build &amp; Thrive
      </p>
      {/* the top of the door carries the World's claim; down here the serious
          word gets its turn — founder's ask */}
      <h2 className="serif max-w-[760px] text-[clamp(1.5rem,2.7vw,2.5rem)] font-light leading-[1.14] text-[#f6ead0]">
        Ambition finally has
        <span className="italic text-[var(--atlas-gold)]"> somewhere to happen.</span>
      </h2>
      <p className="serif mt-5 max-w-[640px] text-base leading-relaxed text-white/55 md:text-lg">
        Not another app to check. A World you walk into, do the work in, and walk
        out of holding something you can actually execute.
      </p>

      <div className="mt-12 grid gap-8 md:grid-cols-3">
        {CARDS(place).map((c, i) => (
          <motion.div
            key={c.n}
            data-testid={`portal-card-${i + 1}`}
            className="flex flex-col gap-3 border-t border-[rgba(224,164,74,0.28)] pt-5"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.65, delay: i * 0.1 }}
          >
            <p className="mono text-[10px] tracking-[0.26em] text-[var(--brand-gold)]">
              {c.n}
            </p>
            <h3 className="engrave text-lg font-semibold tracking-[0.02em] text-[#f2e3c4]">
              {c.t}
            </h3>
            <p className="text-[15px] leading-[1.65] text-white/60">{c.b}</p>
          </motion.div>
        ))}
      </div>

      <a
        href={worldOrigin || "/"}
        data-testid="portal-to-world"
        className="mono mt-14 inline-flex items-center gap-2 rounded-full border border-[rgba(224,164,74,0.5)] px-6 py-3 text-[10px] uppercase tracking-[0.24em] text-[#f2c877] transition-colors duration-300 hover:bg-[#f2c877] hover:text-[#1a0f06]"
      >
        See the whole World <ArrowUpRight size={12} />
      </a>
    </section>

    <JudgesLight />
    <SealedPlaces />

    <SiteFooter />
  </div>
);

export default PortalBelow;
